import { useCallback, useEffect, useRef, useState } from "react";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  invalidResult,
  maxCharactersValidator,
  requiredValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import type {
  ParameterDictionary,
  WorkbookFilter,
  WorkbookFilterSelectedOption,
  WorkbookTemplate,
} from "../../../../../../api/types/workbookRuns.types";

interface FormState {
  name: string;
  selectedTemplate: WorkbookTemplate | undefined;
  filters: WorkbookFilter[];
  dateFilterValidity: Record<string, boolean>;
  touchedFields: ("name" | "selectedTemplate" | "filters")[];
  isValid?: boolean;
  nameResult?: ValidationResult;
  selectedTemplateResult?: ValidationResult;
}

const validateName = combineValidators(requiredValidator, maxCharactersValidator(255));

const validateForm = (form: FormState) => {
  const nameResult = validateName(form.name);
  const allDateFiltersValid = Object.values(form.dateFilterValidity).every((v) => v);
  const isValid = nameResult.isValid && form.selectedTemplate !== undefined && allDateFiltersValid;
  const selectedTemplateResult = form.selectedTemplate
    ? validResult()
    : //As far as we can't select nothing this case should never happen
      invalidResult("Template is required");
  return {
    isValid,
    nameResult,
    selectedTemplateResult,
  };
};

const initDateFilterValidity = (filters: WorkbookFilter[]): Record<string, boolean> => {
  return filters
    .filter((f) => f.type === "Date")
    .reduce(
      (acc, f) => {
        acc[f.id] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
};

const useRunDialogForm = (template: WorkbookTemplate | undefined) => {
  const [form, setForm] = useState<FormState>(() => {
    const filters = template?.workbookFilters ?? [];
    const initForm: FormState = {
      name: template?.name ?? "",
      selectedTemplate: template,
      filters,
      dateFilterValidity: initDateFilterValidity(filters),
      touchedFields: [],
    };
    const validationResult = validateForm(initForm);
    initForm.isValid = validationResult.isValid;
    initForm.nameResult = validationResult.nameResult;
    initForm.selectedTemplateResult = validationResult.selectedTemplateResult;
    return initForm;
  });
  const formRef = useRef<FormState>(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const changeName = useCallback(
    (name: string) => {
      setForm((prev) => {
        const newForm: FormState = {
          ...prev,
          name,
          touchedFields: distinct([...prev.touchedFields, "name"]),
        };
        const validationResult = validateForm(newForm);
        newForm.isValid = validationResult.isValid;
        newForm.nameResult = validationResult.nameResult;
        newForm.selectedTemplateResult = validationResult.selectedTemplateResult;

        return newForm;
      });
    },
    [setForm]
  );

  const changeSelectedTemplate = useCallback(
    (selectedTemplate: WorkbookTemplate) => {
      setForm((prev) => {
        const newForm: FormState = {
          ...prev,
          selectedTemplate: selectedTemplate,
          filters: selectedTemplate.workbookFilters,
          dateFilterValidity: initDateFilterValidity(selectedTemplate.workbookFilters),
          touchedFields: distinct([...prev.touchedFields, "selectedTemplate"]),
        };
        const validationResult = validateForm(newForm);
        newForm.isValid = validationResult.isValid;
        newForm.nameResult = validationResult.nameResult;
        newForm.selectedTemplateResult = validationResult.selectedTemplateResult;

        return newForm;
      });
      if (!formRef.current.name) {
        changeName(selectedTemplate.name);
      }
    },
    [setForm, changeName]
  );

  const updateDateFilter = useCallback(
    (filterId: string, filterValue: string, isValid: boolean) => {
      setForm((prev) => {
        const newForm: FormState = {
          ...prev,
          filters: prev.filters.map((f) => (f.id === filterId ? { ...f, value: [filterValue] } : f)),
          dateFilterValidity: { ...prev.dateFilterValidity, [filterId]: isValid },
          touchedFields: distinct([...prev.touchedFields, "filters"]),
        };
        const validationResult = validateForm(newForm);
        newForm.isValid = validationResult.isValid;
        newForm.nameResult = validationResult.nameResult;
        newForm.selectedTemplateResult = validationResult.selectedTemplateResult;

        return newForm;
      });
    },
    [setForm]
  );

  const updateTextFilter = useCallback(
    (filterId: string, values: ParameterDictionary[]) => {
      const sanitizedValues: WorkbookFilterSelectedOption[] = values.map(({ id, subValues, ...rest }) => ({
        properties: rest,
        subValues,
      }));
      setForm((prev) => {
        const newForm: FormState = {
          ...prev,
          filters: prev.filters.map((f) =>
            f.id === filterId
              ? {
                  ...f,
                  selectedOptions: sanitizedValues,
                  value: sanitizedValues
                    .map((v) => v.properties[f.dictionaryLoadSettings?.primaryKeyField ?? ""])
                    .filter((v) => v !== undefined),
                }
              : f
          ),
          touchedFields: distinct([...prev.touchedFields, "filters"]),
        };
        const validationResult = validateForm(newForm);
        newForm.isValid = validationResult.isValid;
        newForm.nameResult = validationResult.nameResult;
        newForm.selectedTemplateResult = validationResult.selectedTemplateResult;

        return newForm;
      });
    },
    [setForm]
  );

  return {
    name: form.name,
    selectedTemplate: form.selectedTemplate,
    filters: form.filters,
    isValid: form.isValid,
    changeName,
    changeSelectedTemplate,
    updateDateFilter,
    updateTextFilter,
    nameError: form.touchedFields.includes("name") ? form.nameResult?.error : undefined,
  };
};

export default useRunDialogForm;
